/**
 * ScoreBreakdownDialog Component
 *
 * MUI Dialog showing the full score breakdown for validation, comparison, or run scores.
 * Includes a "Recalculate" button for on-demand recalculation.
 *
 * ADD this file to: datatool_fe-QA/src/components/Scoring/ScoreBreakdownDialog.tsx
 */
import React, { useState } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  Box,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  IconButton,
  Chip,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import RefreshIcon from "@mui/icons-material/Refresh";
import {
  IValidationScore,
  IValidationScoreBreakdown,
  IComparisonScoreBreakdown,
  IRunScoreBreakdown,
} from "../../types/Scoring.type";
import ScoreBadge from "./ScoreBadge";

/** Score metadata can exist before all breakdown metrics are available. */
type NullableMetrics<T> = { [K in keyof T]?: T[K] | null };
type BreakdownScore<T> = Pick<IValidationScore, "id" | "score" | "calculated_on"> & {
  score_details?: NullableMetrics<T> | null;
};

type IScoreBreakdownDialogProps = {
  open: boolean;
  onClose: () => void;
  loading?: boolean;
  onRecalculate?: () => Promise<void>;
} & (
  | {
      type: "validation" | "generic_rq";
      scoreData: BreakdownScore<IValidationScoreBreakdown> | null;
    }
  | {
      type: "comparison";
      scoreData: BreakdownScore<IComparisonScoreBreakdown> | null;
    }
  | {
      type: "run";
      scoreData: BreakdownScore<IRunScoreBreakdown> | null;
    }
);

const isNumber = (value: number | null | undefined): value is number =>
  typeof value === "number" && Number.isFinite(value);

const formatPercent = (value: number | null | undefined): string =>
  isNumber(value) ? `${(value * 100).toFixed(4)}%` : "N/A";

const formatNumber = (value: number | null | undefined): string =>
  isNumber(value) ? value.toLocaleString() : "N/A";

const formatFixed = (value: number | null | undefined, digits: number): string =>
  isNumber(value) ? value.toFixed(digits) : "N/A";

const ValidationBreakdownTable: React.FC<{
  details: NullableMetrics<IValidationScoreBreakdown>;
}> = ({ details }) => {
  return (
    <TableContainer component={Paper} variant="outlined" sx={{ mt: 2 }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>
              <strong>Metric</strong>
            </TableCell>
            <TableCell align="right">
              <strong>Low</strong>
            </TableCell>
            <TableCell align="right">
              <strong>Medium</strong>
            </TableCell>
            <TableCell align="right">
              <strong>High</strong>
            </TableCell>
            <TableCell align="right">
              <strong>Total</strong>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow>
            <TableCell>Record Impact</TableCell>
            <TableCell align="right">
              {formatPercent(details.record_impact_low)}
            </TableCell>
            <TableCell align="right">
              {formatPercent(details.record_impact_medium)}
            </TableCell>
            <TableCell align="right">
              {formatPercent(details.record_impact_high)}
            </TableCell>
            <TableCell align="right">
              <strong>{formatPercent(details.total_record_impact)}</strong>
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell>Issue Impact</TableCell>
            <TableCell align="right">
              {formatPercent(details.issue_impact_low)}
            </TableCell>
            <TableCell align="right">
              {formatPercent(details.issue_impact_medium)}
            </TableCell>
            <TableCell align="right">
              {formatPercent(details.issue_impact_high)}
            </TableCell>
            <TableCell align="right">
              <strong>{formatPercent(details.total_issue_impact)}</strong>
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
      <Box sx={{ p: 1.5 }}>
        <Typography variant="body2" color="text.secondary">
          Total Records: {formatNumber(details.total_records)} | Each Record
          Impact: {formatFixed(details.each_records_impact, 8)}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Raw Score: {formatPercent(details.raw_score)} | Final Score:{" "}
          {formatPercent(details.final_score)}
        </Typography>
      </Box>
    </TableContainer>
  );
};

const ComparisonBreakdownTable: React.FC<{
  details: NullableMetrics<IComparisonScoreBreakdown>;
}> = ({ details }) => {
  const validationScores = Array.isArray(details.validation_scores)
    ? details.validation_scores
    : [];
  return (
    <Box sx={{ mt: 2 }}>
      {/* Missing Records Section */}
      <Typography variant="subtitle2" sx={{ mt: 1, mb: 0.5 }}>
        Missing Records
      </Typography>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableBody>
            <TableRow>
              <TableCell>Missing Count</TableCell>
              <TableCell align="right">
                {formatNumber(details.missing_count)}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Count Penalty</TableCell>
              <TableCell align="right">
                {formatPercent(details.missing_count_penalty)}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Blended Penalty</TableCell>
              <TableCell align="right">
                <strong>
                  {formatPercent(details.missing_blended_penalty)}
                </strong>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      {/* Mismatch Records Section */}
      <Typography variant="subtitle2" sx={{ mt: 2, mb: 0.5 }}>
        Mismatch Records
      </Typography>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>
                <strong>Severity</strong>
              </TableCell>
              <TableCell align="right">
                <strong>Count</strong>
              </TableCell>
              <TableCell align="right">
                <strong>Penalty</strong>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell>Low</TableCell>
              <TableCell align="right">
                {formatNumber(details.mismatch_count_low)}
              </TableCell>
              <TableCell align="right">
                {formatPercent(details.mismatch_penalty_low)}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Medium</TableCell>
              <TableCell align="right">
                {formatNumber(details.mismatch_count_medium)}
              </TableCell>
              <TableCell align="right">
                {formatPercent(details.mismatch_penalty_medium)}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>High</TableCell>
              <TableCell align="right">
                {formatNumber(details.mismatch_count_high)}
              </TableCell>
              <TableCell align="right">
                {formatPercent(details.mismatch_penalty_high)}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                <strong>Total</strong>
              </TableCell>
              <TableCell align="right"></TableCell>
              <TableCell align="right">
                <strong>{formatPercent(details.mismatch_total_penalty)}</strong>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      {/* Validation Penalty Section */}
      <Typography variant="subtitle2" sx={{ mt: 2, mb: 0.5 }}>
        Validation Penalty
      </Typography>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableBody>
            <TableRow>
              <TableCell>Resource Validation Scores</TableCell>
              <TableCell align="right">
                {validationScores.map((s, i) => (
                  <Chip
                    key={i}
                    label={formatPercent(s)}
                    size="small"
                    sx={{ mr: 0.5 }}
                  />
                ))}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Validation Penalty</TableCell>
              <TableCell align="right">
                <strong>{formatPercent(details.validation_penalty)}</strong>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ p: 1.5 }}>
        <Typography variant="body2" color="text.secondary">
          Total Records: {formatNumber(details.total_records)}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Raw Score: {formatPercent(details.raw_score)} | Final Score:{" "}
          {formatPercent(details.final_score)}
        </Typography>
      </Box>
    </Box>
  );
};

const RunBreakdownTable: React.FC<{
  details: NullableMetrics<IRunScoreBreakdown>;
}> = ({ details }) => {
  const entries = Array.isArray(details.entries) ? details.entries : [];
  return (
    <Box sx={{ mt: 2 }}>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>
                <strong>Rule</strong>
              </TableCell>
              <TableCell align="right">
                <strong>Score</strong>
              </TableCell>
              <TableCell align="right">
                <strong>Factor</strong>
              </TableCell>
              <TableCell align="right">
                <strong>Weighted</strong>
              </TableCell>
              <TableCell align="center">
                <strong>Status</strong>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {entries.map((entry, idx) => (
              <TableRow key={idx}>
                <TableCell>{entry.rule_name}</TableCell>
                <TableCell align="right">
                  <ScoreBadge score={entry.comparison_score} size="small" />
                </TableCell>
                <TableCell align="right">
                  {formatFixed(entry.factor, 2)}
                </TableCell>
                <TableCell align="right">
                  {formatFixed(entry.weighted_score, 4)}
                </TableCell>
                <TableCell align="center">
                  <Chip
                    label={entry.job_status}
                    size="small"
                    color={
                      entry.job_status === "COMPLETED"
                        ? "success"
                        : entry.job_status === "COMPLETED_WITH_ERRORS"
                          ? "warning"
                          : "error"
                    }
                    variant="outlined"
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ p: 1.5 }}>
        <Typography variant="body2" color="text.secondary">
          Sum of Weighted Scores: {formatFixed(details.sum_weighted_scores, 4)} | Sum
          of Factors: {formatFixed(details.sum_factors, 2)}
        </Typography>
        {details.has_failed_jobs && (
          <Typography variant="body2" color="error">
            Score is 0 because one or more jobs failed or did not complete.
          </Typography>
        )}
        <Typography variant="body2" color="text.secondary">
          Final Score: {formatPercent(details.final_score)}
        </Typography>
      </Box>
    </Box>
  );
};

const ScoreBreakdownDialog: React.FC<IScoreBreakdownDialogProps> = ({
  open,
  onClose,
  type,
  scoreData,
  loading = false,
  onRecalculate,
}) => {
  const [recalculating, setRecalculating] = useState(false);

  const handleRecalculate = async () => {
    if (!onRecalculate) return;
    setRecalculating(true);
    try {
      await onRecalculate();
    } catch (err) {
      console.error(err);
    } finally {
      setRecalculating(false);
    }
  };

  const getTitle = () => {
    switch (type) {
      case "validation":
        return "Validation Score Breakdown";
      case "comparison":
        return "Comparison Score Breakdown";
      case "run":
        return "Run Score Breakdown";
      case "generic_rq":
        return "Generic RQ Score Breakdown";
      default:
        return "Score Breakdown";
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Typography variant="h6">{getTitle()}</Typography>
            {scoreData && (
              <ScoreBadge
                prefixLabel={"Success Rate:"}
                score={scoreData.score}
                size="medium"
              />
            )}
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              py: 6,
              gap: 2,
            }}
          >
            <CircularProgress size={36} />
            <Typography variant="body2" color="text.secondary">
              Loading score breakdown…
            </Typography>
          </Box>
        ) : !scoreData ? (
          <Typography color="text.secondary">
            No score data available.
          </Typography>
        ) : (
          <Box>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Calculated On:{" "}
                  {new Date(scoreData.calculated_on).toLocaleString()}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  align="right"
                >
                  Score ID: {scoreData.id}
                </Typography>
              </Grid>
            </Grid>

            {scoreData.score_details == null ? (
              <Typography color="text.secondary">
                No score data available.
              </Typography>
            ) : (
              <>
                {(type === "validation" || type === "generic_rq") && (
                  <ValidationBreakdownTable details={scoreData.score_details} />
                )}
                {type === "comparison" && (
                  <ComparisonBreakdownTable details={scoreData.score_details} />
                )}
                {type === "run" && (
                  <RunBreakdownTable details={scoreData.score_details} />
                )}
              </>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        {onRecalculate && (
          <Button
            onClick={handleRecalculate}
            disabled={recalculating}
            startIcon={
              recalculating ? <CircularProgress size={16} /> : <RefreshIcon />
            }
            variant="outlined"
          >
            {recalculating ? "Recalculating..." : "Recalculate"}
          </Button>
        )}
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ScoreBreakdownDialog;
